import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCategories, fetchSubCategories } from './store/slices/dressesSlice';
import { selectCartIsOpen, selectCartItems, selectCartTotalAmount } from './store/slices/cartSlice';
import { fetchSiteAlert, selectSiteAlertActive } from './store/slices/siteAlertSlice';
import Header from './components/Header';
import MarketingHeader from './components/MarketingHeader';
import SiteAlertBanner from './components/SiteAlertBanner';
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import CategoryPage from './pages/CategoryPage';
import SubCategoryPage from './pages/SubCategoryPage';
import DiscountedProductsPage from './pages/DiscountedProductsPage';
import NewArrivalPage from './pages/NewArrivalPage';
import MarketingHomePage from './pages/MarketingHomePage';
import AdminRoute from './components/AdminRoute';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import * as roleUtils from './utils/roleUtils';
import CheckoutPage from './pages/CheckoutPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import Cart from './components/Cart';
import Toaster from './components/Toaster';
import MobileBottomNav from './components/MobileBottomNav';
import WhatsAppButton from './components/WhatsAppButton';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import LoadingSpinner from './components/LoadingSpinner';
import { pushPageView, pushViewCart } from './utils/gtm';
import './index.css';

// Admin/back-office pages are only ever visited by staff, but were previously
// bundled into the same JS chunk as the public storefront — meaning every
// storefront visitor (including the homepage) downloaded the entire admin
// dashboard (charts, drag-and-drop, spreadsheet export, etc.) before first
// paint. Lazy-loading them splits that code into separate chunks fetched only
// when someone actually navigates under /admin.
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const ProductAdminPage = lazy(() => import('./pages/ProductAdminPage'));
const CategoriesPage = lazy(() => import('./pages/CategoriesPage'));
const SubCategoriesPage = lazy(() => import('./pages/SubCategoriesPage'));
const CouriersPage = lazy(() => import('./pages/CouriersPage'));
const StartOrderPage = lazy(() => import('./pages/StartOrderPage'));
const ProcessingOrdersPage = lazy(() => import('./pages/ProcessingOrdersPage'));
const ReadyOrdersPage = lazy(() => import('./pages/ReadyOrdersPage'));
const ShippedOrdersPage = lazy(() => import('./pages/ShippedOrdersPage'));
const DeliveredOrdersPage = lazy(() => import('./pages/DeliveredOrdersPage'));
const CompletedOrdersPage = lazy(() => import('./pages/CompletedOrdersPage'));
const ReturnedOrdersPage = lazy(() => import('./pages/ReturnedOrdersPage'));
const ReturnedFromCourierOrdersPage = lazy(() => import('./pages/ReturnedFromCourierOrdersPage'));
const CancelledOrdersPage = lazy(() => import('./pages/CancelledOrdersPage'));
const ProductFormPage = lazy(() => import('./pages/ProductFormPage'));
const AdminLayout = lazy(() => import('./components/AdminLayout'));
const MarketingLayout = lazy(() => import('./components/MarketingLayout'));
const BannerContentsPage = lazy(() => import('./pages/BannerContentsPage'));
const MiscPhotosPage = lazy(() => import('./pages/MiscPhotosPage'));
const NewArrivalSettingsPage = lazy(() => import('./pages/NewArrivalSettingsPage'));
const SiteAlertSettingsPage = lazy(() => import('./pages/SiteAlertSettingsPage'));
const CategoryOrderPage = lazy(() => import('./pages/CategoryOrderPage'));
const ReviewManagementPage = lazy(() => import('./pages/ReviewManagementPage'));
const BestSellersPage = lazy(() => import('./pages/BestSellersPage'));
const ProductVideosPage = lazy(() => import('./pages/ProductVideosPage'));
const CelebrityManagementPage = lazy(() => import('./pages/CelebrityManagementPage'));
const PromoCodesPage = lazy(() => import('./pages/PromoCodesPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const SuppliersPage = lazy(() => import('./pages/SuppliersPage'));
const StoresPage = lazy(() => import('./pages/StoresPage'));
const KPIDashboardPage = lazy(() => import('./pages/KPIDashboardPage'));
const AccountsKPIPage = lazy(() => import('./pages/AccountsKPIPage'));
const SimpleDashboardPage = lazy(() => import('./pages/SimpleDashboardPage'));
const UserListPage = lazy(() => import('./pages/UserListPage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const UnauthorizedPage = lazy(() => import('./pages/UnauthorizedPage'));
const StockTransactionsPage = lazy(() => import('./pages/StockTransactionsPage'));
const FeaturedProductsPage = lazy(() => import('./pages/FeaturedProductsPage'));
const ExchangeRequestsPage = lazy(() => import('./pages/ExchangeRequestsPage'));
const ExchangedOrdersPage = lazy(() => import('./pages/ExchangedOrdersPage'));
const RequestedOrdersPage = lazy(() => import('./pages/RequestedOrdersPage'));
const ReturnedProductEntriesPage = lazy(() => import('./pages/ReturnedProductEntriesPage'));

const AdminSuspenseFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner />
  </div>
);

// The marketing page is the storefront's home; it brings its own header
// (with a transparent hero-overlay effect). Every other storefront page uses
// the same header in its plain/solid form, except the all-products page,
// which keeps the original search-focused Header.
const StorefrontLayout = () => {
  const location = useLocation();
  const isProductsPage = location.pathname === '/products';
  const isHomePage = location.pathname === '/';
  const needsHeaderOffset = !isProductsPage && !isHomePage;
  const isCartOpen = useSelector(selectCartIsOpen);
  const cartItems = useSelector(selectCartItems);
  const cartTotalAmount = useSelector(selectCartTotalAmount);
  const alertActive = useSelector(selectSiteAlertActive);

  useEffect(() => {
    pushPageView(location.pathname + location.search);
  }, [location]);

  useEffect(() => {
    if (isCartOpen) {
      pushPageView(location.pathname + location.search, 'cart');
      pushViewCart(cartItems, cartTotalAmount);
    }
  }, [isCartOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`min-h-screen bg-gray-50 flex flex-col ${alertActive ? 'pt-10' : ''}`}>
      <SiteAlertBanner />
      {isProductsPage ? <Header /> : <MarketingHeader />}
      <main className={`flex-1 pb-16 md:pb-0 ${needsHeaderOffset ? 'md:pt-20' : ''}`}>
        <Routes>
          <Route path="/" element={<MarketingHomePage />} />
          <Route path="/products" element={<HomePage />} />
          <Route path="/product/:id" element={<ProductPage />} />
          <Route path="/category/:id" element={<CategoryPage />} />
          <Route path="/subcategory/:id" element={<SubCategoryPage />} />
          <Route path="/discounts" element={<DiscountedProductsPage />} />
          <Route path="/new-arrival" element={<NewArrivalPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          {/* Kept for anyone with an old bookmark/link */}
          <Route path="/marketing" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <Cart />
      <Toaster />
      <MobileBottomNav />
      <WhatsAppButton />
    </div>
  );
};

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchSubCategories());
    dispatch(fetchSiteAlert());
  }, [dispatch]);

  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<AdminSuspenseFallback />}>
        <Routes>
        {/* Admin Login Route - No Header/Cart */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* Marketing Area - Protected layout with its own sidebar */}
        <Route
          path="/admin/marketing/*"
          element={
            <AdminRoute>
              <RoleProtectedRoute checkPermission={roleUtils.canManageMarketing}>
                <MarketingLayout />
              </RoleProtectedRoute>
            </AdminRoute>
          }
        >
          <Route index element={<Navigate to="banner-contents" replace />} />
          <Route path="banner-contents" element={<BannerContentsPage />} />
          <Route path="misc-photos" element={<MiscPhotosPage />} />
          <Route path="new-arrival" element={<NewArrivalSettingsPage />} />
          <Route path="site-alert" element={<SiteAlertSettingsPage />} />
          <Route path="category-order" element={<CategoryOrderPage />} />
          <Route path="reviews" element={<ReviewManagementPage />} />
          <Route path="best-sellers" element={<BestSellersPage />} />
          <Route path="product-videos" element={<ProductVideosPage />} />
          <Route path="celebrities" element={<CelebrityManagementPage />} />
          <Route path="promo-codes" element={<PromoCodesPage />} />
        </Route>

        {/* Admin Area - Protected layout with nested routes */}
        <Route
          path="/admin/*"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<SimpleDashboardPage />} />
          <Route path="KPI" element={
            <RoleProtectedRoute checkPermission={roleUtils.canAccessKPI}>
              <KPIDashboardPage />
            </RoleProtectedRoute>
          } />
          <Route path="accounts-kpi" element={
            <RoleProtectedRoute checkPermission={roleUtils.canAccessKPI}>
              <AccountsKPIPage />
            </RoleProtectedRoute>
          } />
          <Route path="products" element={
            <RoleProtectedRoute checkPermission={roleUtils.canViewProducts}>
              <ProductAdminPage />
            </RoleProtectedRoute>
          } />
          <Route path="products/new" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageProducts}>
              <ProductFormPage />
            </RoleProtectedRoute>
          } />
          <Route path="products/:id/edit" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageProducts}>
              <ProductFormPage />
            </RoleProtectedRoute>
          } />
          <Route path="stock-transactions" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageProducts}>
              <StockTransactionsPage />
            </RoleProtectedRoute>
          } />
          <Route path="featured-products" element={
            <RoleProtectedRoute checkPermission={roleUtils.canViewSensitiveProductData}>
              <FeaturedProductsPage />
            </RoleProtectedRoute>
          } />
          <Route path="start-order" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageOrders}>
              <StartOrderPage />
            </RoleProtectedRoute>
          } />
          <Route path="orders/requested" element={
            <RoleProtectedRoute checkPermission={roleUtils.canViewRequestedOrders}>
              <RequestedOrdersPage />
            </RoleProtectedRoute>
          } />
          <Route path="orders/processing" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageOrders}>
              <ProcessingOrdersPage />
            </RoleProtectedRoute>
          } />
          <Route path="orders/ready" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageOrders}>
              <ReadyOrdersPage />
            </RoleProtectedRoute>
          } />
          <Route path="orders/shipped" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageOrders}>
              <ShippedOrdersPage />
            </RoleProtectedRoute>
          } />
          <Route path="orders/delivered" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageOrders}>
              <DeliveredOrdersPage />
            </RoleProtectedRoute>
          } />
          <Route path="orders/completed" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageOrders}>
              <CompletedOrdersPage />
            </RoleProtectedRoute>
          } />
          <Route path="orders/returned" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageOrders}>
              <ReturnedOrdersPage />
            </RoleProtectedRoute>
          } />
          <Route path="orders/returned-from-courier" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageOrders}>
              <ReturnedFromCourierOrdersPage />
            </RoleProtectedRoute>
          } />
          <Route path="orders/cancelled" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageOrders}>
              <CancelledOrdersPage />
            </RoleProtectedRoute>
          } />
          <Route path="orders/exchanges" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageOrders}>
              <ExchangeRequestsPage />
            </RoleProtectedRoute>
          } />
          <Route path="orders/exchanged" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageOrders}>
              <ExchangedOrdersPage />
            </RoleProtectedRoute>
          } />
          <Route path="orders/returns-to-receive" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageOrders}>
              <ReturnedProductEntriesPage />
            </RoleProtectedRoute>
          } />
          <Route path="categories" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageSuppliersCategories}>
              <CategoriesPage />
            </RoleProtectedRoute>
          } />
          <Route path="subcategories" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageSuppliersCategories}>
              <SubCategoriesPage />
            </RoleProtectedRoute>
          } />
          <Route path="couriers" element={
            <RoleProtectedRoute checkPermission={roleUtils.canAccessCouriers}>
              <CouriersPage />
            </RoleProtectedRoute>
          } />
          <Route path="suppliers" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageSuppliersCategories}>
              <SuppliersPage />
            </RoleProtectedRoute>
          } />
          <Route path="stores" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageSuppliersCategories}>
              <StoresPage />
            </RoleProtectedRoute>
          } />
          <Route path="customers" element={
            <RoleProtectedRoute checkPermission={roleUtils.canManageCustomers}>
              <CustomersPage />
            </RoleProtectedRoute>
          } />
          <Route path="users" element={
            <RoleProtectedRoute checkPermission={roleUtils.canAccessUserManagement}>
              <UserListPage />
            </RoleProtectedRoute>
          } />
          <Route path="change-password" element={<ChangePasswordPage />} />
          <Route path="unauthorized" element={<UnauthorizedPage />} />
        </Route>

        {/* Main Storefront Routes - With Header/Cart */}
        <Route path="/*" element={<StorefrontLayout />} />
      </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
